enum WidgetType {
  METRIC = 'metric',
  PIE_CHART = 'pie_chart',
  DONUT_CHART = 'donut_chart',
  LINE_CHART = 'line_chart',
  BAR_CHART = 'bar_chart',
  TABLE = 'table',
}

export enum WidgetId {
  WORKFLOWS_COUNT = 'workflows_count',
  COMPANIES_COUNT = 'companies_count',
  USERS_COUNT = 'users_count',
  AVERAGE_DURATION = 'average_duration',
  ADVANCED_PLANNING = 'advanced_planning',
  EQUIPMENT_BOOKINGS_WORKFLOWS = 'equipment_bookings_workflows',
  EQUIPMENT_BOOKINGS_HOURS = 'equipment_bookings_hours',
  LOCATIONS_WORKFLOWS = 'locations_workflows',
  LOCATIONS_BY_TYPE = 'locations_by_type',
  WORKFLOWS_BY_STATUS = 'workflows_by_status',
  ACTIVE_VS_COMPLETED = 'active_vs_completed',
  WORKFLOWS_BY_CATEGORY = 'workflows_by_category',
  WORKFLOWS_TIMELINE = 'workflows_timeline',
  RECENT_WORKFLOWS = 'recent_workflows',
}

interface WidgetDataQuery {
  collection: string
  filters?: Record<string, any>
  aggregation: string
  time_period?: string
  fields?: string[]
}

interface WidgetVisualizationConfig {
  dataKey?: string
  nameKey?: string
  colors?: string[]
  xAxisLabel?: string
  yAxisLabel?: string
  unit?: string
}

interface WidgetConfig {
  id: WidgetId | string // Allow string for custom widgets
  type: WidgetType
  title: string
  defaultLayout: {
    x: number
    y: number
    w: number
    h: number
    minW?: number
    minH?: number
    maxW?: number
    maxH?: number
  }
  isVisible: boolean
  availableChartTypes?: WidgetType[] // Optional: alternative chart types user can select
  isCustom?: boolean // Flag for AI-generated custom widgets
  description?: string // Description for custom widgets
  data_query?: WidgetDataQuery // Data query specification for custom widgets
  visualization_config?: WidgetVisualizationConfig // Visualization config for custom widgets
}

export const WIDGET_CONFIGS: WidgetConfig[] = [
  // ========== LEFT COLUMN (3 units wide) ==========

  // Top widget in left column
  {
    id: WidgetId.WORKFLOWS_COUNT,
    type: WidgetType.METRIC,
    title: 'TOTAL WORKFLOWS',
    defaultLayout: {
      x: 0,
      y: 0,
      w: 3,
      h: 3,
      minW: 3,
      minH: 3,
      maxH: 4,
    },
    isVisible: true,
  },
  {
    id: WidgetId.COMPANIES_COUNT,
    type: WidgetType.METRIC,
    title: 'COMPANIES',
    defaultLayout: {
      x: 0,
      y: 3,
      w: 3,
      h: 3,
      minW: 3,
      minH: 3,
      maxH: 4,
    },
    isVisible: true,
  },
  {
    id: WidgetId.USERS_COUNT,
    type: WidgetType.METRIC,
    title: 'USERS (EDITORS)',
    defaultLayout: {
      x: 0,
      y: 6,
      w: 3,
      h: 4,
      minW: 3,
      minH: 3,
      maxH: 5,
    },
    isVisible: true,
  },
  {
    id: WidgetId.AVERAGE_DURATION,
    type: WidgetType.METRIC,
    title: 'AVERAGE DURATION',
    defaultLayout: {
      x: 0,
      y: 10,
      w: 3,
      h: 3,
      minW: 3,
      minH: 3,
      maxH: 4,
    },
    isVisible: true,
  },
  {
    id: WidgetId.ADVANCED_PLANNING,
    type: WidgetType.METRIC,
    title: 'ADVANCED PLANNING',
    defaultLayout: {
      x: 0,
      y: 13,
      w: 3,
      h: 4,
      minW: 3,
      minH: 3,
      maxH: 5,
    },
    isVisible: true,
  },

  // ========== RIGHT COLUMN (9 units wide) ==========

  // Large widget at top right
  {
    id: WidgetId.WORKFLOWS_TIMELINE,
    type: WidgetType.LINE_CHART,
    title: 'WORKFLOWS TIMELINE',
    defaultLayout: {
      x: 3,
      y: 0,
      w: 9,
      h: 5,
      minW: 6,
      minH: 4,
    },
    isVisible: true,
    availableChartTypes: [WidgetType.LINE_CHART, WidgetType.BAR_CHART],
  },

  // Two medium widgets side by side
  {
    id: WidgetId.WORKFLOWS_BY_STATUS,
    type: WidgetType.PIE_CHART,
    title: 'WORKFLOWS BY STATUS',
    defaultLayout: {
      x: 3,
      y: 5,
      w: 4,
      h: 5,
      minW: 4,
      minH: 5,
    },
    isVisible: true,
    availableChartTypes: [
      WidgetType.PIE_CHART,
      WidgetType.BAR_CHART,
      WidgetType.DONUT_CHART,
    ],
  },
  {
    id: WidgetId.ACTIVE_VS_COMPLETED,
    type: WidgetType.DONUT_CHART,
    title: 'ACTIVE VS COMPLETED',
    defaultLayout: {
      x: 7,
      y: 5,
      w: 5,
      h: 5,
      minW: 4,
      minH: 5,
    },
    isVisible: true,
    availableChartTypes: [
      WidgetType.DONUT_CHART,
      WidgetType.PIE_CHART,
      WidgetType.BAR_CHART,
    ],
  },

  // Large widget below
  {
    id: WidgetId.WORKFLOWS_BY_CATEGORY,
    type: WidgetType.BAR_CHART,
    title: 'WORKFLOWS BY CATEGORY',
    defaultLayout: {
      x: 3,
      y: 8,
      w: 9,
      h: 5,
      minW: 6,
      minH: 4,
    },
    isVisible: true,
    availableChartTypes: [
      WidgetType.BAR_CHART,
      WidgetType.PIE_CHART,
      WidgetType.DONUT_CHART,
    ],
  },

  // Large widget at bottom
  {
    id: WidgetId.RECENT_WORKFLOWS,
    type: WidgetType.TABLE,
    title: 'RECENT WORKFLOWS',
    defaultLayout: {
      x: 3,
      y: 12,
      w: 9,
      h: 5,
      minW: 6,
      minH: 4,
    },
    isVisible: true,
  },

  // ========== LOCATION ANALYTICS (Row 7) ==========
  {
    id: WidgetId.LOCATIONS_WORKFLOWS,
    type: WidgetType.BAR_CHART,
    title: 'LOCATIONS (WORKFLOWS)',
    defaultLayout: {
      x: 0,
      y: 23,
      w: 6,
      h: 5,
      minW: 4,
      minH: 4,
    },
    isVisible: true,
    availableChartTypes: [
      WidgetType.BAR_CHART,
      WidgetType.PIE_CHART,
      WidgetType.DONUT_CHART,
    ],
  },
  {
    id: WidgetId.LOCATIONS_BY_TYPE,
    type: WidgetType.PIE_CHART,
    title: 'LOCATIONS (BY TYPE)',
    defaultLayout: {
      x: 6,
      y: 23,
      w: 6,
      h: 5,
      minW: 4,
      minH: 4,
    },
    isVisible: true,
    availableChartTypes: [
      WidgetType.PIE_CHART,
      WidgetType.BAR_CHART,
      WidgetType.DONUT_CHART,
    ],
  },
  // ========== EQUIPMENT & RESOURCES (Row 8) ==========
  {
    id: WidgetId.EQUIPMENT_BOOKINGS_WORKFLOWS,
    type: WidgetType.METRIC,
    title: 'EQUIPMENT BOOKINGS',
    defaultLayout: {
      x: 0,
      y: 27,
      w: 6,
      h: 3,
      minW: 3,
      minH: 3,
      maxH: 4,
    },
    isVisible: true,
  },
  {
    id: WidgetId.EQUIPMENT_BOOKINGS_HOURS,
    type: WidgetType.METRIC,
    title: 'EQUIPMENT HOURS',
    defaultLayout: {
      x: 6,
      y: 27,
      w: 6,
      h: 3,
      minW: 3,
      minH: 3,
      maxH: 4,
    },
    isVisible: true,
  },
]

export const getDefaultLayout = () => {
  return WIDGET_CONFIGS.map(config => ({
    i: config.id,
    ...config.defaultLayout,
  }))
}
